import React, { useState, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './Faktura.css';

/**
 * InvoiceGenerator Component
 * BIH invoice form with optional VAT, portrait preview, dropdown for ID type, Serbian date format, and PDF export.
 */
const InvoiceGenerator = () => {
  const [invoiceData, setInvoiceData] = useState({
    company: { name: '', idType: 'JMBG', idNumber: '', address: '' },
    customer: { name: '', idType: 'JMBG', idNumber: '', address: '' },
    brojFakture: '',
    datum: new Date().toISOString().slice(0, 10),
    rokPlacanja: '',
    items: [{ description: '', quantity: 1, price: 0, taxPercent: 17 }],
  });
  const [includeVAT, setIncludeVAT] = useState(true);
  const invoiceRef = useRef();

  // Format date to Serbian locale (dd.MM.yyyy)
  const formatDate = dateStr => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('sr-BA');
  };

  const handleCompanyChange = e => {
    const { name, value } = e.target;
    setInvoiceData(prev => ({
      ...prev,
      company: { ...prev.company, [name]: value }
    }));
  };

  const handleCustomerChange = e => {
    const { name, value } = e.target;
    setInvoiceData(prev => ({
      ...prev,
      customer: { ...prev.customer, [name]: value }
    }));
  };

  const handleFieldChange = e => {
    const { name, value } = e.target;
    setInvoiceData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    setInvoiceData(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [name]: name === 'description' ? value : parseFloat(value) || 0 };
      return { ...prev, items };
    });
  };

  const addItem = () => setInvoiceData(prev => ({
    ...prev,
    items: [...prev.items, { description: '', quantity: 1, price: 0, taxPercent: 17 }]
  }));

  const removeItem = idx => setInvoiceData(prev => ({
    ...prev,
    items: prev.items.filter((_, i) => i !== idx)
  }));

  const subtotal = invoiceData.items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const totalTax = includeVAT
    ? invoiceData.items.reduce((sum, it) => sum + it.price * it.quantity * it.taxPercent / 100, 0)
    : 0;
  const total = subtotal + totalTax;

  const generatePDF = () => {
    html2canvas(invoiceRef.current, { scale: 2 }).then(canvas => {
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, 'PNG', 0, 0, w, h);
      pdf.save(`faktura_${invoiceData.brojFakture || 'nova'}.pdf`);
    });
  };

  return (
    <div className="invoice-generator">
      <form className="invoice-form" onSubmit={e => { e.preventDefault(); generatePDF(); }}>
        <h2>Podaci o fakturi</h2>

        <label>
          <input
            type="checkbox"
            checked={includeVAT}
            onChange={e => setIncludeVAT(e.target.checked)}
          /> PDV obveznik
        </label>

        <label>Broj fakture:</label>
        <input name="brojFakture" value={invoiceData.brojFakture} onChange={handleFieldChange} required />

        <label>Datum izdavanja:</label>
        <input
          type="date"
          name="datum"
          value={invoiceData.datum}
          onChange={handleFieldChange}
          required
        />

        <label>Rok plaćanja:</label>
        <input
          type="date"
          name="rokPlacanja"
          value={invoiceData.rokPlacanja}
          onChange={handleFieldChange}
        />

        <h3>Prodavac</h3>
        <label>Naziv:</label>
        <input name="name" value={invoiceData.company.name} onChange={handleCompanyChange} required />
        <label>Tip identifikatora:</label>
        <select name="idType" value={invoiceData.company.idType} onChange={handleCompanyChange}>
          <option value="JMBG">JMBG</option>
          <option value="PIB">PIB</option>
          <option value="JIB">JIB</option>
        </select>
        <label>{invoiceData.company.idType}:</label>
        <input name="idNumber" value={invoiceData.company.idNumber} onChange={handleCompanyChange} />
        <label>Adresa:</label>
        <input name="address" value={invoiceData.company.address} onChange={handleCompanyChange} />

        <h3>Kupac</h3>
        <label>Naziv kupca:</label>
        <input name="name" value={invoiceData.customer.name} onChange={handleCustomerChange} required />
        <label>Tip identifikatora:</label>
        <select name="idType" value={invoiceData.customer.idType} onChange={handleCustomerChange}>
          <option value="JMBG">JMBG</option>
          <option value="PIB">PIB</option>
          <option value="JIB">JIB</option>
        </select>
        <label>{invoiceData.customer.idType} kupca:</label>
        <input name="idNumber" value={invoiceData.customer.idNumber} onChange={handleCustomerChange} />
        <label>Adresa kupca:</label>
        <input name="address" value={invoiceData.customer.address} onChange={handleCustomerChange} required />

        <h3>Stavke</h3>
        {invoiceData.items.map((it, idx) => (
          <div key={idx}>
            <label>Opis:</label>
            <input name="description" value={it.description} onChange={e => handleItemChange(idx, e)} required />
            <label>Količina:</label>
            <input type="number" name="quantity" value={it.quantity} onChange={e => handleItemChange(idx, e)} min="1" />
            <label>Cijena:</label>
            <input type="number" name="price" value={it.price} onChange={e => handleItemChange(idx, e)} step="0.01" />
            {includeVAT && (
              <>
                <label>PDV %:</label>
                <input type="number" name="taxPercent" value={it.taxPercent} onChange={e => handleItemChange(idx, e)} />
              </>
            )}
            <button type="button" className="remove-item" onClick={() => removeItem(idx)}>Obriši stavku</button>
          </div>
        ))}
        <button type="button" className="add-item" onClick={addItem}>Dodaj stavku</button>
        <button type="submit">Preuzmi PDF</button>
      </form>

      <div className="invoice-preview" ref={invoiceRef}>
        <h1 style={{ textAlign: 'center' }}>FAKTURA</h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <div>
            <h3>Prodavac</h3>
            <p>Naziv: {invoiceData.company.name}</p>
            <p>{invoiceData.company.idType}: {invoiceData.company.idNumber}</p>
            <p>Adresa: {invoiceData.company.address}</p>
          </div>
          <div>
            <h3>Kupac</h3>
            <p>Naziv kupca: {invoiceData.customer.name}</p>
            <p>{invoiceData.customer.idType} kupca: {invoiceData.customer.idNumber}</p>
            <p>Adresa kupca: {invoiceData.customer.address}</p>
          </div>
        </div>
        <div style={{ marginTop: '20px' }}>
          <p><strong>Broj fakture:</strong> {invoiceData.brojFakture}</p>
          <p><strong>Datum izdavanja:</strong> {formatDate(invoiceData.datum)}</p>
          <p><strong>Rok plaćanja:</strong> {formatDate(invoiceData.rokPlacanja)}</p>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
          <thead>
            <tr>
              {['Opis', 'Količina', 'Cijena', ...(includeVAT ? ['PDV (%)'] : []), 'Ukupno'].map(h => (
                <th key={h} style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoiceData.items.map((it, idx) => {
              const net = it.price * it.quantity;
              const lineTotal = includeVAT ? net * (1 + it.taxPercent/100) : net;
              return (
                <tr key={idx}>
                  <td style={{ border: '1px solid #000', padding: '8px' }}>{it.description}</td>
                  <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>{it.quantity}</td>
                  <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>{it.price.toFixed(2)}</td>
                  {includeVAT && <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>{it.taxPercent}</td>}
                  <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>{lineTotal.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="totals" style={{ textAlign: 'right', marginTop: '20px' }}>
          <p><strong>Osnovica:</strong> {subtotal.toFixed(2)}</p>
          {includeVAT && <p><strong>Ukupno PDV:</strong> {totalTax.toFixed(2)}</p>}
          <h3>Ukupno za platiti: {total.toFixed(2)}</h3>
        </div>
      </div>
    </div>
  );
};

export default InvoiceGenerator;